<template>
  <VNavigationDrawer
    ref="navigationDrawer"
    v-model="isActive"
    class="the-navigation-menu pt-8 text-max-emphasis"
    :rail="!mobile && !isExpanded"
    theme="TwDark"
    width="270"
    rail-width="80"
    :touchless="true"
    floating
    color="axle-navbar-top"
  >
    <template #prepend>
      <div class="mb-md-6 px-5">
        <VIcon
          color="primary"
          icon="$twi-app-gm"
          size="36"
        />
      </div>
      <TheNavigationTeamSwitcherMenu
        :is-expanded="isExpanded"
      />
    </template>
    <div class="d-flex flex-column align-stretch the-navigation-menu__content pt-4">
      <VList
        class="px-5"
        density="compact"
        nav
      >
        <VListItem
          v-if="!mobile"
          :prepend-icon="isExpanded ? 'close' : 'menu'"
          :title="t(`navigationMenu.${ isExpanded ? 'closeMenu' : 'expandMenu' }`)"
          data-testid="mobileButtonToggleMenu"
          @click="isExpanded = !isExpanded"
        >
          <VTooltip
            v-if="!isExpanded"
            activator="parent"
            location="right"
            :aria-label="t('navigationMenu.expandMenu')"
          >
            {{ t('navigationMenu.expandMenu') }}
          </VTooltip>
        </VListItem>
        <TheNavigationMenuItem
          v-for="item in mainItems"
          :key="item.id"
          :nav-item="item"
          :is-expanded="isExpanded"
        />
      </VList>
      <VList
        class="px-5 py-1 mt-auto"
        density="compact"
        nav
      >
        <UserManagement
          v-if="isUserManagementEnabled && hasHubApp"
          :is-expanded="isExpanded"
        />
        <HelpMenu
          :is-expanded="isExpanded"
        />
        <AppSwitcherListItem
          :is-expanded="isExpanded"
        />
      </VList>
    </div>
    <template #append>
      <UserMenu
        :is-expanded="isExpanded"
      />
    </template>
    <template #image>
      <div class="the-navigation-menu__burst">
        <ImageBackgroundBurst />
      </div>
    </template>
  </VNavigationDrawer>
</template>

<script setup lang="ts">
import ImageBackgroundBurst from '@/components/navigation/ImageBackgroundBurst.vue';
import { onBeforeMount, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import { usePrimaryNavigation } from '@/composables/usePrimaryNavigation';
import AppSwitcherListItem from '@/components/navigation/TheNavigationMenuAppSwitcher.vue';
import TheNavigationMenuItem from '@/components/navigation/TheNavigationMenuItem.vue';
import HelpMenu from '@/components/navigation/TheNavigationMenuHelpMenu.vue';
import UserMenu from '@/components/navigation/TheNavigationMenuUserMenu.vue';
import TheNavigationTeamSwitcherMenu from '@/components/navigation/TheNavigationTeamSwitcherMenu.vue';
import UserManagement from '@/components/navigation/TheNavigationMenuUserManagement.vue';
import { useLDFlag } from 'launchdarkly-vue-client-sdk';
import { FeatureFlags } from '@/types/featureFlags';
import { useAppSwitcher } from '@/composables/useAppSwitcher';
import { TEAMWORKS_APP_SLUGS } from '@teamworksdev/vuetify';


const isActive = defineModel<boolean>({ default: false });

const { t } = useI18n();
const isUserManagementEnabled = useLDFlag(FeatureFlags.TUM_REDIRECT_ENABLED, false);
const { mainItems } = usePrimaryNavigation();
const { appSwitcher } = useAppSwitcher();

const hasHubApp = computed(() => appSwitcher.value.hasApp(TEAMWORKS_APP_SLUGS.HUB));

const isExpanded = ref(true);

const { mobile } = useDisplay();
onBeforeMount(async () => {
  if (!mobile.value) {
    window.requestAnimationFrame(() => {
      isActive.value = true;
    });
  }
});

</script>

<style scoped lang="scss">
.the-navigation-menu {
    transition:background-color 0.2s;
    :deep(.v-navigation-drawer__img){
        overflow: hidden;
    }
    :deep(.the-navigation-menu__burst){
        color:rgb(var(--v-theme-axle-navbar-bottom));
        position: absolute;
        bottom: -8px;
        left: 0;
        width: 270px;
        transition:color 0.2s;
    }
    &__content{
        min-height: 100%;
    }
}
</style>
